/**
 * Feed pins (SCH-2657): up to three posts pinned to the top of a group or
 * section-space feed. Storage is Stream Feeds v3 native pins.
 *
 * `activity` is the same enriched feed item a page of
 * `GET /v1/feeds/:feedId/activities` carries, passed through unvalidated for
 * the same reason as `FeedActivitiesPageResponseSchema` (./activities-page):
 * validating it would strip the fields the feed card renders.
 */

import { z } from 'zod'
import type { ExtendedFeedActivity } from './common'

const isRecord = (value: unknown): boolean => typeof value === 'object' && value !== null

/** The most live pins one feed may hold. A pin over the cap is refused, never rotated in. */
export const FEED_PIN_MAX = 3 as const

export const FeedPinSchema = z.object({
  /** ISO timestamp of when the post was pinned. */
  pinnedAt: z.string(),
  /** Who pinned it, or null when the pin was made by the platform system user. */
  pinnedBy: z.object({ id: z.string(), name: z.string() }).nullable(),
  activity: z.custom<ExtendedFeedActivity>(isRecord)
})

export type FeedPin = z.infer<typeof FeedPinSchema>

export const ListFeedPinsResponseSchema = z.object({
  /** Newest pin first. */
  pins: z.array(FeedPinSchema),
  viewerCanPin: z.boolean(),
  maxPins: z.literal(FEED_PIN_MAX)
})

export type ListFeedPinsResponse = z.infer<typeof ListFeedPinsResponseSchema>

export const AddFeedPinRequestSchema = z.object({
  /** The `ContentRecord.publicId` of the post to pin. */
  contentId: z.string().min(1)
})

export type AddFeedPinRequest = z.infer<typeof AddFeedPinRequestSchema>

/** 409 body when the feed already holds `maxPins` live pins. Carries the current pins so the client can offer a swap. */
export const FeedPinLimitReachedErrorSchema = z.object({
  code: z.literal('PIN_LIMIT_REACHED'),
  message: z.string(),
  pins: z.array(FeedPinSchema),
  maxPins: z.literal(FEED_PIN_MAX)
})

export type FeedPinLimitReachedError = z.infer<typeof FeedPinLimitReachedErrorSchema>
